import type { JSX } from "react";
import { getBasePath } from "./base-path";
import type { UptimeBucket } from "./types";

export const ranges = ["12h", "24h", "7d", "14d", "30d", "90d"] as const;

function barColor(bucket: UptimeBucket): string {
  if (bucket.no_data) return "bar-nodata";
  if (bucket.worst_status === "under_maintenance") return "bar-maintenance";
  const pct = bucket.uptime_percentage;
  if (pct >= 99) return "bar-operational";
  if (pct >= 95) return "bar-degraded";
  if (pct >= 90) return "bar-partial";
  return "bar-major";
}

function toOverallUptime(buckets: UptimeBucket[]): string {
  const data = buckets.filter((v) => !v.no_data);
  if (data.length === 0) return "--";
  const avg = data.reduce((acc, it) => acc + it.uptime_percentage, 0) / data.length;
  return `${avg.toFixed(2)}% uptime`;
}

export function StatusBars({ id, range, buckets }: { id: string; range: string; buckets: UptimeBucket[] }): JSX.Element {
  const columns = Math.max(buckets.length, 1);

  return (
    <div style={{ marginTop: "0.5lh" }}>
      <div style={{ display: "grid", height: "0.5lh", gap: "1px", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {buckets.map((bucket, idx) => {
          const incidentIDs = bucket.incident_ids ?? [];
          const incidentNames = bucket.incidents ?? [];
          const tooltipLines = [
            bucket.label || bucket.date,
            `${bucket.uptime_percentage.toFixed(2)}% uptime`,
          ];
          if (incidentIDs.length > 0) {
            tooltipLines.push(`${incidentIDs.length} incident${incidentIDs.length > 1 ? "s" : ""}`);
          }
          return (
            <div
              key={`${id}-${idx}`}
              title={tooltipLines.join("\n")}
              style={{ position: "relative" }}
            >
              <div className={barColor(bucket)} style={{ height: "0.5lh" }} />
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: "0.25lh", display: "flex", justifyContent: "space-between", fontSize: "0.85em", color: "var(--foreground2)" }}>
        <span>{range}</span>
        <span>{toOverallUptime(buckets)}</span>
        <span>Now</span>
      </div>
    </div>
  );
}
